import parse from "date-fns/parse";
import { useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { VERIFICATION_TRANSACTIONS_REFERENCE_DATA_TYPES, VerificationTransactionsReferenceData } from "@constants/reference-data";
import { AXN_COUNTRY_NULL_PROPERTY, NULL_ENCODED_VALUE } from "@constants/verification-transaction-values";
import { resources } from "@constants/hardcoded-strings";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { VerificationTransactionsSearchFiltersState } from "@idw-types/state/verification-transactions-state";
import { setVerificationTransactionsSearchFilters } from "@redux/actions/verification-transactions-actions";
import { resetTableSettingsTableState } from "@redux/actions/table-settings-actions";
import { initialVerificationTransactionsState } from "@redux/reducers/verification-transactions-reducer";
import { getVerificationTransactionsSearchResultsReduxRequest } from "@redux-requests/verification-transactions";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { downloadVerificationTransactionsFile } from "@utils/file-util";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CardContainer } from "@containers/common/card-container/card-container";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { VerificationTransactionsSearchForm } from "./verification-transactions-search-form/verification-transactions-search-form";
import { VerificationTransactionsTableContainer } from "./verification-transactions-table-container/verification-transactions-table-container";
import { VerificationTransactionsSummaryContainer } from "./verification-transactions-summary-container/verification-transactions-summary-container";
import { getSessionState, getTableSettingsState, getVerificationTransactionsState } from "@redux/selectors/root-selectors";

export const VerificationTransactionsContainer = () => {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const [abortController, setAbortController] = useState<AbortController>(null);

    const { session } = useSelector(getSessionState);
    const { verificationTransactions: verificationTransactionsTableState } = useSelector(getTableSettingsState);
    const {
        searchFilters,
        searchResults
    } = useSelector(getVerificationTransactionsState);
    const referenceData = useReferenceDataSelector<VerificationTransactionsReferenceData>(VERIFICATION_TRANSACTIONS_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.verificationTransactions && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                VERIFICATION_TRANSACTIONS_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.VERIFICATION_TRANSACTIONS
            );
        }
    }, [referenceData.isLoading.verificationTransactions, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCountries &&
            referenceData.axnAssertions &&
            referenceData.axnCompanies &&
            referenceData.axnRPServicesWithSOIDs &&
            referenceData.axnRelyingParties &&
            referenceData.axnSelectedServiceOfferingSOs &&
            referenceData.claimStatuses &&
            referenceData.policyDecisions &&
            referenceData.simpleAXNRPServiceWorkflows &&
            referenceData.simpleAXNWorkflows &&
            referenceData.apAssertions &&
            referenceData.documentationLinks
        );
    }, [
        referenceData.axnAssertions,
        referenceData.axnCompanies,
        referenceData.axnRPServicesWithSOIDs,
        referenceData.axnRelyingParties,
        referenceData.axnSelectedServiceOfferingSOs,
        referenceData.claimStatuses,
        referenceData.policyDecisions,
        referenceData.simpleAXNRPServiceWorkflows,
        referenceData.simpleAXNWorkflows,
        referenceData.axnCountries,
        referenceData.apAssertions,
        referenceData.documentationLinks
    ]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.verificationTransactions;
    }, [referenceData.isLoading.verificationTransactions]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const allowedRelyingParties = useMemo(() => {
        if (referenceData.axnRelyingParties) {
            return extractRelyingPartiesForPropertyName(
                null,
                session.user.axnAdminUserRPProperties,
                referenceData.axnRelyingParties
            );
        }

        return [];
    }, [
        session.user.axnAdminUserRPProperties,
        referenceData.axnRelyingParties
    ]);

    const allowedSimpleAxnWorkflows = useMemo(() => {
        if (referenceData.simpleAXNWorkflows) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.simpleAXNWorkflows.filter(saw => rpIds.includes(saw.relyingPartyID));
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.simpleAXNWorkflows
    ]);

    const allowedSimpleAxnRpServiceWorkflows = useMemo(() => {
        if (referenceData.simpleAXNRPServiceWorkflows) {
            const workflowIds = allowedSimpleAxnWorkflows.map(w => w.id);
            return referenceData.simpleAXNRPServiceWorkflows.filter(rpsw =>
                workflowIds.includes(rpsw.workflowID)
            );
        }

        return [];
    }, [
        allowedSimpleAxnWorkflows,
        referenceData.simpleAXNRPServiceWorkflows
    ]);

    const allowedRpServicesWithSOIDs = useMemo(() => {
        if (referenceData.axnRPServicesWithSOIDs) {
            const rpIds = allowedRelyingParties.map(rp => rp.id);
            return referenceData.axnRPServicesWithSOIDs.filter(rps =>
                rpIds.includes(`${rps.relyingPartyID}`)
            );
        }

        return [];
    }, [
        allowedRelyingParties,
        referenceData.axnRPServicesWithSOIDs
    ]);

    useEffect(() => {
        if (Array.from(searchParams.entries()).length > 0 && isLoaded) {
            const shouldSearchOnLoad = isStringTrue(searchParams.get("shouldSearchOnLoad"));

            const initialState = initialVerificationTransactionsState.searchFilters;

            const policyDecisions: Array<string> = [];
            (searchParams.get("policyDecisions")?.split(",") || initialState.policyDecisions).forEach(policyDecision => {
                const foundPolicyDecision = referenceData.policyDecisions.find(pd => pd.value === policyDecision);
                if (foundPolicyDecision) {
                    policyDecisions.push(foundPolicyDecision.key);
                }
            });

            const newSearchFilters: VerificationTransactionsSearchFiltersState = {
                credentials: searchParams.get("credentials") || initialState.credentials,
                serviceOfferingIds: searchParams.get("serviceOfferingIds")?.split(",") || initialState.serviceOfferingIds,
                assertionIds: searchParams.get("assertionIds")?.split(",") || initialState.assertionIds,
                claimStatuses: searchParams.get("claimStatuses")?.split(",") || initialState.claimStatuses,
                companyIds: searchParams.get("companyIds")?.split(",") || initialState.companyIds,
                relyingPartyIds: searchParams.get("relyingPartyIds")?.split(",") || initialState.relyingPartyIds,
                rpServiceIds: searchParams.get("rpServiceIds")?.split(",") || initialState.rpServiceIds,
                appId: searchParams.get("appId") || initialState.appId,
                transactionId: searchParams.get("transactionId") || initialState.transactionId,
                policyDecisions: policyDecisions,
                sessionKey: searchParams.get("sessionKey") || initialState.sessionKey,
                workflowIds: searchParams.get("workflowIds")?.split(",") || initialState.workflowIds,
                timeRangeType: (searchParams.get("timeRangeType") || initialState.timeRangeType) as TimeRangeTypes,
                startDate: searchParams.has("startDate")
                    ? parse(searchParams.get("startDate"), SERVER_DATE_TIME_FORMAT, new Date())
                    : initialState.startDate,
                endDate: searchParams.has("endDate")
                    ? parse(searchParams.get("endDate"), SERVER_DATE_TIME_FORMAT, new Date())
                    : initialState.endDate,
                includeArchivedServicesAndWorkflows: searchParams.has("includeArchivedServicesAndWorkflows")
                    ? isStringTrue(searchParams.get("includeArchivedServicesAndWorkflows"))
                    : initialState.includeArchivedServicesAndWorkflows,
                countries: searchParams.has("country") ? searchParams.get("country")?.split(",") : initialState.countries
            };

            if (shouldSearchOnLoad) {
                handleSearch(newSearchFilters);
            } else {
                dispatch(setVerificationTransactionsSearchFilters(newSearchFilters));
            }
        }
    }, [searchParams, isLoaded]);

    const handleSearch = async (newSearchFilters: VerificationTransactionsSearchFiltersState) => {
        const controller = new AbortController();
        setAbortController(controller);
        const {
            credentials,
            serviceOfferingIds,
            assertionIds,
            claimStatuses,
            companyIds,
            relyingPartyIds,
            rpServiceIds,
            appId,
            transactionId,
            policyDecisions,
            sessionKey,
            workflowIds,
            startDate,
            endDate,
            countries
        } = newSearchFilters;

        dispatch(resetTableSettingsTableState(TableSettingsTableTypes.VERIFICATION_TRANSACTIONS));
        dispatch(setVerificationTransactionsSearchFilters(newSearchFilters));

        const apiKeys = referenceData.axnRPServicesWithSOIDs
            .filter(rpService => rpServiceIds.includes(rpService.id))
            .map(rpService => rpService.apiKey);

        const appIds: Array<string> = [];

        if (appId?.includes(",")) {
            const parsedAppIds = appId.split(",");
            parsedAppIds.forEach(parsedAppId => {
                if (parsedAppId && parsedAppId.toLowerCase() === "null") {
                    appIds.push(NULL_ENCODED_VALUE);
                } else if (parsedAppId) {
                    appIds.push(parsedAppId)
                }
            });
        } else if (appId && appId.toLowerCase() === "null") {
            appIds.push(NULL_ENCODED_VALUE);
        } else if (appId) {
            appIds.push(appId);
        }

        getVerificationTransactionsSearchResultsReduxRequest(dispatch, controller.signal)(
            credentials,
            serviceOfferingIds,
            assertionIds,
            claimStatuses,
            companyIds,
            relyingPartyIds,
            apiKeys,
            appIds,
            transactionId,
            policyDecisions,
            sessionKey,
            workflowIds,
            startDate,
            endDate,
            referenceData.axnRelyingParties,
            countries
        );
    };

    const handleCancelSearch = () => {
        abortController.abort();

    }

    const onClick = async () => {
        if (searchResults.verificationTransactions) {
            downloadVerificationTransactionsFile(
                `Verification_Transactions_Export`,
                searchResults.verificationTransactions
            );
        }
    };

    const headerButton = useMemo(() => {
        return (
            <Button
                id="verification-transaction-export-to-csv-button"
                title={resources.common.buttons.exportToCSV.label}
                onClick={onClick}
                disabled={
                    !isLoaded ||
                    searchResults.isLoading ||
                    !searchResults.verificationTransactions?.length
                }
            >
                {resources.common.buttons.exportToCSV.label}
            </Button>
        );
    }, [isLoaded, searchResults.isLoading, searchResults.verificationTransactions]);


    const countries = useMemo(() => {
        if (isReferenceDataPresent) {
            return [AXN_COUNTRY_NULL_PROPERTY, ...referenceData.axnCountries];
        }

        return [];
    }, [isReferenceDataPresent, referenceData.axnCountries]);

    return (
        <CardContainer>
            <ContentPageHeader
                title={resources.pages.verificationTransactions.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"verification-transaction-list-page-header"}
                    />
                }
                headerButton={headerButton}
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <VerificationTransactionsSearchForm
                        requestInProgress={searchResults.isLoading}
                        assertions={referenceData.axnAssertions}
                        claimStatuses={referenceData.claimStatuses}
                        companies={referenceData.axnCompanies}
                        apAssertions={referenceData.apAssertions}
                        countries={countries}
                        relyingParties={allowedRelyingParties}
                        rpServicesWithSOIDs={allowedRpServicesWithSOIDs}
                        selectedServiceOfferingSOs={referenceData.axnSelectedServiceOfferingSOs}
                        simpleAxnWorkflows={allowedSimpleAxnWorkflows}
                        simpleAxnRpServiceWorkflows={allowedSimpleAxnRpServiceWorkflows}
                        policyDecisions={referenceData.policyDecisions}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                        handleCancelSearch={handleCancelSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.summary && (
                    <VerificationTransactionsSummaryContainer verificationTransactionSummary={searchResults.summary} />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.verificationTransactions && (
                    <VerificationTransactionsTableContainer
                        verificationTransactions={searchResults.verificationTransactions}
                        tableState={verificationTransactionsTableState}
                    />
                )}
                {isLoaded && !searchResults.isLoading && (searchResults.summary?.transactionCount || 0) > 40000 && (
                    <Alert variant={"danger"}>{resources.pages.verificationTransactions.transactionLimitMessage}</Alert>
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};